const internals = {};

internals.connectionVisibilityOptions = [
    { value: 'anyone', label: 'Anyone' },
    { value: 'peers', label: 'Connected peers' },
    { value: 'me', label: 'Only me' }
];

internals.yesNoOptions = [
    { value: 'on', label: 'Yes' },
    { value: 'off', label: 'No' }
];

internals.digestIntervalOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'none', label: 'Never' }
];

internals.selectFieldDefinitions = {
    connectionsVisibility: {
        label: 'Who should be able to see your connections?',
        options: internals.connectionVisibilityOptions
    },
    messageNotifications: {
        label: 'Would you like to receive direct message notifications?',
        options: internals.yesNoOptions
    },
    groupVisibility: {
        label: 'Who should be able to see your groups?',
        options: internals.connectionVisibilityOptions
    },
    groupMsgNotifications: {
        label: 'Would you like to receive group chat notifications?',
        options: internals.yesNoOptions
    },
    digestInterval: {
        label: 'How often would you like to receive digest emails of recent activity?',
        options: internals.digestIntervalOptions
    },
    pendingConnections: {
        label: 'Would you like to receive email reminders about connection requests?',
        options: internals.yesNoOptions
    },
    careerAlerts: {
        label: 'Are you interested in alerts about internships, jobs, and related opportunities based on your profile?',
        options: internals.yesNoOptions
    },
    offerAlerts: {
        label: 'Would you like to receive special offers in your notifications and digest emails?',
        options: internals.yesNoOptions
    }
};

internals.sections = [
    {
        header: 'communications',
        title: 'Communication',
        fields: ['profileEmail']
    },
    {
        header: 'connections',
        title: 'Connections',
        fields: ['connectionsVisibility', 'messageNotifications']
    },
    {
        header: 'groups',
        title: 'Groups',
        fields: ['groupVisibility', 'groupMsgNotifications']
    },
    {
        header: 'emails',
        title: 'Emails',
        fields: ['digestInterval', 'pendingConnections']
    },
    {
        header: 'offers',
        title: 'Offers',
        fields: ['offerAlerts']
    },
    {
        header: 'other',
        title: 'Other',
        fields: ['careerAlerts']
    }
];

internals.getDefaults = ({
    prefs,
    hideJobAlerts,
    isEditPage,
    isSignupPage
}) => {

    const {
        groupVisibility,
        connectionsVisibility,
        digestInterval,
        pendingConnections,
        careerAlerts,
        offerAlerts,
        profileEmail,
        groupMsgNotifications,
        hideInterestPopup,
        messageNotifications
    } = prefs || {};

    const isSignupOrEditPage = isSignupPage || isEditPage;

    return {
        profileEmail: profileEmail || '',
        connectionsVisibility: connectionsVisibility || 'anyone',
        groupVisibility: groupVisibility || (isSignupOrEditPage ? 'anyone' : ''),
        digestInterval: digestInterval || (isSignupOrEditPage ? 'daily' : ''),
        pendingConnections: pendingConnections || 'on',
        careerAlerts: hideJobAlerts ? 'off' : (careerAlerts || 'on'),
        offerAlerts: offerAlerts || 'on',
        groupMsgNotifications: groupMsgNotifications || (isSignupOrEditPage ? 'on' : ''),
        hideInterestPopup: hideInterestPopup || 'false',
        messageNotifications: messageNotifications || 'on'
    };
};

internals.addTitle = ({
    fields,
    noDivider,
    title
}) => {

    fields.push({
        type: 'title',
        text: title,
        noDivider
    });
};

internals.buildSelectField = ({
    label,
    name,
    notifyChange,
    options
}) => ({
    type: 'SelectField',
    name,
    label,
    options,
    fullWidth: true,
    onChange: (value) => notifyChange({ [name]: value })
});

internals.buildProfileEmailField = ({
    methodsRef,
    notifyChange,
    validate
}) => ({
    type: 'TextField',
    name: 'profileEmail',
    label: 'Backup Email',
    fullWidth: true,
    textFieldProps: {
        type: 'email',
        inputProps: { 'aria-label': 'Backup email input' }
    },
    rules: {
        validate: (value) => validate('profileEmail', {
            ...methodsRef.current?.getValues?.(),
            profileEmail: value
        }) || true
    },
    onChange: (value) => notifyChange({ profileEmail: value })
});

internals.buildField = ({
    field,
    methodsRef,
    notifyChange,
    validate
}) => {

    if (field === 'profileEmail') {
        return internals.buildProfileEmailField({
            methodsRef,
            notifyChange,
            validate
        });
    }

    const selectField = internals.selectFieldDefinitions[field];

    if (!selectField) {
        return null;
    }

    return internals.buildSelectField({
        name: field,
        notifyChange,
        ...selectField
    });
};

internals.buildFields = ({
    fieldsToShow = [],
    headersToShow = [],
    hideJobAlerts,
    methodsRef,
    noDivider,
    notifyChange,
    validate
}) => {
    const fields = [];
    const enabledFields = new Set(fieldsToShow);
    const enabledHeaders = new Set(headersToShow);

    internals.sections.forEach((section) => {

        if (!enabledHeaders.has(section.header)) {
            return;
        }

        if (section.header === 'other' && hideJobAlerts) {
            return;
        }

        internals.addTitle({
            fields,
            title: section.title,
            noDivider
        });

        section.fields.forEach((field) => {

            if (!enabledFields.has(field)) {
                return;
            }

            if (field === 'careerAlerts' && hideJobAlerts) {
                return;
            }

            const builtField = internals.buildField({
                field,
                methodsRef,
                notifyChange,
                validate
            });

            if (builtField) {
                fields.push(builtField);
            }
        });
    });

    return fields;
};

module.exports.buildFormConfig = function buildFormConfig(props) {

    const {
        onSubmit
    } = props;

    return {
        onSubmit,
        fields: internals.buildFields(props),
        defaults: internals.getDefaults(props),
        formOptions: {
            mode: 'onChange'
        }
    };
};

module.exports.internals = internals;
